import type { ReactNode } from 'react';

import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { OverflowTooltip } from 'components/data-display/OverflowTooltip';
import { RouterLink } from 'components/navigation/RouterLink';

export type PageHeaderBreadcrumb = {
  title: string;
  /** Omit on the current page — the trailing crumb renders as plain text, not a link. */
  link?: string;
};

/**
 * Breadcrumbs and a back link are the two variants of the same navigation slot in the design, so
 * they are mutually exclusive here rather than both being optional booleans.
 */
type PageHeaderNavigation =
  | { breadcrumbs: PageHeaderBreadcrumb[]; back?: never }
  | { back: { label?: string; to: string }; breadcrumbs?: never }
  | { breadcrumbs?: never; back?: never };

type PageHeaderProps = PageHeaderNavigation & {
  title: string;
  /** Rendered right after the title, e.g. a status chip. */
  titleBadge?: ReactNode;
  description?: ReactNode;
  /** Right-aligned primary action(s), rendered on the title row. */
  actions?: ReactNode;
  disableDivider?: boolean;
  'data-testid'?: string;
};

/**
 * Identifies the current page: optional breadcrumbs or back link, a title, an optional
 * description, an optional primary action, and a closing divider.
 *
 * Content-area controls (tabs, search, view toggles) belong in ContentHeader, not here.
 */
export function PageHeader({
  title,
  titleBadge,
  description,
  actions,
  breadcrumbs,
  back,
  disableDivider = false,
  'data-testid': dataTestId = 'page-header'
}: PageHeaderProps) {
  return (
    <Box data-testid={dataTestId}>
      <Stack sx={{ px: 3, py: 3, gap: 1 }}>
        {breadcrumbs && <PageHeaderBreadcrumbs items={breadcrumbs} />}

        {back && (
          <Box>
            <Button
              component={RouterLink}
              to={back.to}
              size='small'
              startIcon={<ArrowBackOutlined />}
              data-testid={`${dataTestId}-back`}
            >
              {back.label ?? 'Back'}
            </Button>
          </Box>
        )}

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap={2}
          // Keeps the title row the same height whether or not it carries an action button, so
          // headers do not shift vertically between pages.
          sx={{ minHeight: 40 }}
        >
          <Stack direction='row' alignItems='center' gap={1} sx={{ minWidth: 0 }}>
            {/* Focusable so the full-title tooltip also opens from the keyboard. */}
            <OverflowTooltip title={title} tabIndex={0} data-testid={`${dataTestId}-title`}>
              {/* Inline so the wrapper's ellipsis applies to it, while staying an <h5> heading. */}
              <Typography variant='h5' sx={{ display: 'inline' }}>
                {title}
              </Typography>
            </OverflowTooltip>
            {titleBadge}
          </Stack>
          {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
        </Stack>

        {description && <Typography variant='body1'>{description}</Typography>}
      </Stack>

      {!disableDivider && <Divider />}
    </Box>
  );
}

function PageHeaderBreadcrumbs({ items }: { items: PageHeaderBreadcrumb[] }) {
  return (
    <Breadcrumbs
      aria-label='breadcrumb'
      sx={{
        '& .MuiBreadcrumbs-separator': { color: 'text.secondary' },
        // One line: crumbs shrink and end in an ellipsis instead of wrapping.
        '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
        '& .MuiBreadcrumbs-li': { minWidth: 0 }
      }}
    >
      {items.map((item, index) => (
        // A plain crumb has no focusable link inside, so the wrapper takes focus for the keyboard tooltip.
        <OverflowTooltip key={`${item.title}-${index}`} title={item.title} tabIndex={item.link ? undefined : 0}>
          {item.link ? (
            <Link component={RouterLink} to={item.link} variant='body1' color='text.primary' underline='hover'>
              {item.title}
            </Link>
          ) : (
            <Typography variant='body1' component='span' color='text.primary'>
              {item.title}
            </Typography>
          )}
        </OverflowTooltip>
      ))}
    </Breadcrumbs>
  );
}
